import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetUserDetailsQuery,
  useUpdateUserMutation,
} from "../../slices/usersApiSlice";

const UserEditScreen = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [isActive, setIsActive] = useState(true);

  const { data: user, isLoading, error } = useGetUserDetailsQuery(userId);

  const [updateUser, { isLoading: loadingUpdate }] = useUpdateUserMutation();

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setRole(user.role || "");
      setPhone(user.phone || "");
      setAddress(user.address || "");
      setIsActive(user.isActive ?? true);
    }
  }, [user]);

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      await updateUser({
        userId,

        name,
        email,
        phone,
        address,
        role,
        isActive,
      }).unwrap();

      toast.success("User updated successfully");

      navigate("/admin/userlist");
    } catch (err) {
      const message = err?.data?.message || err?.error || "User update failed";

      switch (message) {
        case "User not found":
          toast.error("User does not belong to this coaching center.");
          break;

        case "Phone already exists":
          toast.error("Another user already uses this phone number.");
          break;

        case "Invalid role":
          toast.error("Invalid user role selected.");
          break;

        default:
          toast.error(message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/admin/userlist"
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl bg-white shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Users
        </Link>

        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl border border-white/30 p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Edit User
            </h1>

            <p className="text-gray-500 mt-2">
              Update coaching center user information
            </p>
          </div>

          {loadingUpdate && (
            <div className="mb-6 rounded-xl bg-blue-100 text-blue-700 p-4 text-center">
              Updating user...
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-10 text-gray-600">
              Loading user details...
            </div>
          ) : error ? (
            <div className="rounded-xl bg-red-100 text-red-700 p-4 text-center">
              {error?.data?.message || error.error}
            </div>
          ) : (
            <form
              onSubmit={submitHandler}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {/* Name */}
              <Input label="Full Name" value={name} setValue={setName} />

              {/* Email */}
              <Input
                label="Email"
                type="email"
                value={email}
                setValue={setEmail}
              />

              {/* Phone */}
              <Input label="Phone" value={phone} setValue={setPhone} />

              {/* Role */}

              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Role
                </label>

                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
                >
                  <option value="student">Student</option>

                  <option value="manager">Manager</option>

                  <option value="coachingAdmin">Coaching Admin</option>

                  <option value="productAdmin">Product Admin</option>
                </select>
              </div>

              {/* Status */}

              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Account Status
                </label>

                <select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value === "true")}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
                >
                  <option value="true">Active</option>

                  <option value="false">Inactive</option>
                </select>
              </div>

              {/* Address */}

              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Address
                </label>

                <textarea
                  rows="4"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 resize-none"
                />
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={loadingUpdate}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60"
                >
                  {loadingUpdate ? "Updating..." : "Update User"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value, setValue, type = "text" }) => (
  <div>
    <label className="block mb-2 text-sm font-semibold text-gray-700">
      {label}
    </label>

    <input
      type={type}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
    />
  </div>
);

export default UserEditScreen;
